import React from "react"; // Added React import
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, MessageSquare, User, CalendarCheck, Calendar, Newspaper, GraduationCap } from "lucide-react"; // Added Newspaper
import { useAuth } from "utils/AuthContext"; // Use centralized auth
import { useChatStore } from "utils/chatStore"; // Added chat store
import { conversationHasUnread } from "utils/chatService";
import { APP_BASE_PATH } from "app"; // Added APP_BASE_PATH

// Define the structure for a navigation link for the bottom bar
interface BottomNavLink {
  to: string;
  icon: React.ElementType;
  label: string;
  roles: Array<"staff" | "client" | "admin" | "limited_access" | "all" | "guest">; // Roles that can see this link - "guest" for non-authenticated users
  showBadge?: boolean; // Added badge support
}

// Define the links specifically for the bottom nav
const bottomNavLinks: BottomNavLink[] = [
  {
    to: "/", // Use explicit path for Home
    icon: LayoutDashboard,
    label: "Home",
    roles: ["all"], // Show to everyone - both logged-in and non-authenticated
  },
  {
    to: "/chat", // Add chat to bottom nav
    icon: MessageSquare,
    label: "Chat",
    roles: ["admin", "staff"],
    showBadge: true, // Enable badge for chat
  },
  {
    to: "/client-chat", // Add client chat to bottom nav
    icon: MessageSquare,
    label: "Chat",
    roles: ["client"],
    showBadge: true, // Enable badge for client chat
  },
  {
    to: "/news", // Add news to bottom nav
    icon: Newspaper,
    label: "News",
    roles: ["guest"], // Only show to non-authenticated users
  },
  {
    to: "/events", // Add events to bottom nav
    icon: Calendar,
    label: "Events",
    roles: ["guest"], // Only show to non-authenticated users
  },
  {
    to: "/myappointments", // Path for My Appointments
    icon: CalendarCheck,
    label: "Appointments", // Shortened label
    roles: ["client", "limited_access"], // Only for clients and limited_access
  },
  {
    to: "/training", // LMS / training catalog for staff
    icon: GraduationCap,
    label: "Training",
    roles: ["staff", "admin"],
  },
  {
    to: "/UserProfile", // Assuming this is the profile page path
    icon: User,
    label: "Profile",
    roles: ["staff", "client", "admin"], // Only show to logged-in users
  },
];


// Keep original NavItemProps interface name if other components use it by chance
interface NavItemProps {
  to: string;
  icon: React.ElementType;
  label: string;
  badgeCount?: number; // Added badge count
}

// Updated NavItem to use APP_BASE_PATH and NavLink's isActive
const NavItem: React.FC<NavItemProps> = ({ to, icon: IconComponent, label, badgeCount }) => {
  // Construct full path WITH base path
  const fullPath = `${APP_BASE_PATH}${to}`.replace("//", "/"); // Ensure no double slashes

  return (
    <NavLink
      to={to} // Reverted from fullPath for debugging
      // Use NavLink's isActive prop for simpler styling logic
      className={({ isActive }) =>
        `flex flex-col items-center justify-center w-full h-full relative ${
          isActive
            ? "text-blue-600 dark:text-blue-400" // Adjusted active colors
            : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        } transition-colors duration-200`
      }
      // Add end prop for more precise matching, especially for "/"
      end={to === "/"}
    >
      <div className="relative">
        <IconComponent className="h-6 w-6 mb-1" />
        {/* Badge for unread count */}
        {badgeCount && badgeCount > 0 && (
          <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
            {badgeCount > 99 ? '99+' : badgeCount}
          </div>
        )}
      </div>
      <span className="text-xs font-medium">{label}</span>
    </NavLink>
  );
};

export function BottomNavBar() {
  // Fetch user and role from centralized auth
  const { user, role, loading } = useAuth();
  
  // Get conversations from chat store
  const { conversations } = useChatStore();
  
  // Function to count unread messages for current user
  const getUnreadCount = () => {
    if (!user?.uid || !conversations) return 0;
    // Shared team read-state — counter-first (see chatService).
    return conversations.filter(conversation => conversationHasUnread(conversation, user.uid)).length;
  };

  // Filter links based on role
  const filteredLinks = role 
    ? bottomNavLinks.filter(
        (link) => link.roles.includes("all") || link.roles.includes(role as any),
      )
    : bottomNavLinks.filter((link) => link.roles.includes("all") || link.roles.includes("guest")); // Show "all" and "guest" links when not logged in

  const unreadCount = getUnreadCount();

  return (
    // Added md:hidden to hide on larger screens
    <nav className="fixed bottom-0 left-0 right-0 z-50 h-16 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-t-lg md:hidden">
      <div className="flex justify-around items-stretch h-full">
        {/* Render filtered links */}
        {filteredLinks.map((link) => (
          <NavItem
            key={link.to}
            to={link.to}
            icon={link.icon}
            label={link.label}
            badgeCount={link.showBadge && unreadCount > 0 ? unreadCount : undefined}
          />
        ))}
      </div>
    </nav>
  );
}
